import type { Shape } from "./types";

export interface SeatSlot {
  cx: number; // local center x
  cy: number; // local center y
  w: number;
  h: number;
  rot: number; // local rotation in deg (added to the workstation's own rotation)
}

/**
 * Seat positions in the workstation's local coordinate frame (0,0 = top-left
 * of the workstation bounding box). Geometry mirrors the rendering in
 * ItemShape.tsx for the workstation-* shapes.
 */
export function getSeatSlots(shape: Shape, w: number, h: number, seats?: number): SeatSlot[] {
  switch (shape) {
    case "workstation": {
      const n = Math.max(2, seats ?? 12);
      const pairs = Math.max(1, Math.round(n / 2));
      const chairW = Math.min(50, w * 0.22);
      const rowH = h / pairs;
      const seatPad = Math.min(6, rowH * 0.1);
      const slots: SeatSlot[] = [];
      for (let i = 0; i < pairs; i++) {
        const cy = rowH * i + seatPad + (rowH - seatPad * 2) / 2;
        // left chair faces right (into the desk)
        slots.push({ cx: 2 + (chairW - 4) / 2, cy, w: chairW - 4, h: rowH - seatPad * 2, rot: 90 });
        // right chair faces left
        slots.push({ cx: w - chairW + 2 + (chairW - 4) / 2, cy, w: chairW - 4, h: rowH - seatPad * 2, rot: 270 });
      }
      return slots.slice(0, n);
    }
    case "workstation-linear": {
      const n = Math.max(1, seats ?? 5);
      const deskDepth = Math.min(60, w * 0.55);
      const chairW = w - deskDepth;
      const rowH = h / n;
      const seatPad = Math.min(6, rowH * 0.1);
      const slots: SeatSlot[] = [];
      for (let i = 0; i < n; i++) {
        const cy = rowH * i + seatPad + (rowH - seatPad * 2) / 2;
        slots.push({
          cx: deskDepth + 2 + (chairW - 4) / 2,
          cy,
          w: chairW - 4,
          h: rowH - seatPad * 2,
          rot: 270,
        });
      }
      return slots;
    }
    case "workstation-l": {
      const total = Math.max(2, seats ?? 6);
      const perArm = Math.max(1, Math.floor(total / 2));
      const seatW = Math.min(60, w / (perArm + 1), h / (perArm + 1));
      const depth = Math.min(60, Math.min(w, h) * 0.3);
      const slots: SeatSlot[] = [];
      // horizontal arm — chairs face south
      for (let i = 0; i < perArm; i++) {
        const cw = seatW * 0.8;
        const ch = Math.max(10, h - depth - 4);
        slots.push({
          cx: depth + i * seatW + seatW * 0.1 + cw / 2,
          cy: depth + 2 + ch / 2,
          w: cw,
          h: ch,
          rot: 180,
        });
      }
      // vertical arm — chairs face east
      for (let i = 0; i < perArm; i++) {
        const cw = Math.max(10, w - depth - 4);
        const ch = seatW * 0.8;
        slots.push({
          cx: depth + 2 + cw / 2,
          cy: depth + i * seatW + seatW * 0.1 + ch / 2,
          w: cw,
          h: ch,
          rot: 90,
        });
      }
      return slots;
    }
    case "workstation-corner": {
      const depth = Math.min(60, Math.min(w, h) * 0.4);
      const chairSize = Math.min(50, Math.min(w, h) * 0.28);
      return [{
        cx: (depth + w) / 2,
        cy: (depth + h) / 2,
        w: chairSize,
        h: chairSize,
        rot: 225,
      }];
    }
    default:
      return [];
  }
}

/** SVG path d for the desk part of a workstation, drawn in local coords. */
export function getDeskPath(shape: Shape, w: number, h: number): string {
  switch (shape) {
    case "workstation": {
      const chairW = Math.min(50, w * 0.22);
      const deskBlockW = w - chairW * 2;
      return `M ${chairW} 0 L ${chairW + deskBlockW} 0 L ${chairW + deskBlockW} ${h} L ${chairW} ${h} Z`;
    }
    case "workstation-linear": {
      const deskDepth = Math.min(60, w * 0.55);
      return `M 0 0 L ${deskDepth} 0 L ${deskDepth} ${h} L 0 ${h} Z`;
    }
    case "workstation-l":
    case "workstation-corner": {
      const factor = shape === "workstation-l" ? 0.3 : 0.4;
      const depth = Math.min(60, Math.min(w, h) * factor);
      return `M 0 0 L ${w} 0 L ${w} ${depth} L ${depth} ${depth} L ${depth} ${h} L 0 ${h} Z`;
    }
    default:
      return `M 0 0 L ${w} 0 L ${w} ${h} L 0 ${h} Z`;
  }
}

/** Hatched unoccupiable corner zone for L-shaped workstations. */
export function getCornerZone(shape: Shape, w: number, h: number): { x: number; y: number; w: number; h: number } | null {
  if (shape === "workstation-l" || shape === "workstation-corner") {
    const factor = shape === "workstation-l" ? 0.3 : 0.4;
    const depth = Math.min(60, Math.min(w, h) * factor);
    return { x: 0, y: 0, w: depth, h: depth };
  }
  return null;
}

export function isWorkstationShape(shape: Shape): boolean {
  return (
    shape === "workstation" ||
    shape === "workstation-linear" ||
    shape === "workstation-l" ||
    shape === "workstation-corner"
  );
}

export function rotateAround(px: number, py: number, cx: number, cy: number, deg: number) {
  const rad = (deg * Math.PI) / 180;
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  const dx = px - cx;
  const dy = py - cy;
  return { x: cx + dx * c - dy * s, y: cy + dx * s + dy * c };
}